<template>
  <NewBsBox
    :bs="{
      display: 'inline-flex',
      minWidth: 178 * levelNums + (levelNums - 1) + 2,
      border: '1px solid #D9D9D9',
      ...bs,
    }"
  >
    <template
      v-for="(prevContext, index) of contexts"
      :key="`context-${prevContext ? prevContext.lookupKey : index}`"
    >
      <Divider
        v-if="index !== 0"
        :vertical="true"
        :bs="{ bg: '#d9d9d9' }"
      />
      <CascadeLevel
        :level="index + 1"
        :prev-context="prevContext"
        :filter-able="filterAble"
      >
        <template
          v-if="prevContext"
          #default="{ keyword }"
        >
          <Option
            v-for="item of prevContext.children"
            v-show="keyword ? item[textKey].indexOf(keyword) > -1 : true"
            :key="item[identKey]"
            :data-active="isSelected(item, index)"
            @click="e => handleSelect(item, index, e)"
          >
            {{ item[textKey] }}
            <template
              v-if="!isLeafCheck(item)"
              #right
            >
              <BeerIcon
                :size="14"
                :bs="{ minWidth: 14, ml: 4 }"
                icon="arrow-right-center-m"
                data-nav="true"
                color="mute"
              />
            </template>
          </Option>
        </template>
      </CascadeLevel>
    </template>
  </NewBsBox>
</template>

<script>
  import { isNil } from 'lodash'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import { Divider } from '@xhs/yam-beer/cmp/common'
  import Option from '@xhs/yam-beer/cmp/overlayers/Option/Option.vue'
  import {
    getContext,
    ROOT_KEY,
    loadChildrenAndSet,
    getLookupkey,
  } from '@xhs/yam-beer/utils/cascadeUtils'
  import CascadeLevel from './CascadeLevel.vue'

  /**
   * 平铺的级联选择控件，空间充足的时候考虑使用这个组件
   */
  export default {
    name: 'CascadeTree',
    components: {
      NewBsBox,
      BeerIcon,
      Option,
      CascadeLevel,
      Divider,
    },
    props: {
      bs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 获取对应级别的选项 Promise 接口
       * @param {object} conf
       *        {object} conf.level 需要获取的 level 数， level 从 1 开始
       *        {object} conf.context 该 level 下选中 parent 的选项
       * @returns {promise}
       *          {object[]} promise.resolve 返回的选项列表，给 textKey, identKey, isLeaf 使用
       *          {error} promise.reject 出错的时候，会显示选项获取失败
       */
      getTree: {
        type: Function,
        required: true,
      },
      /**
       * 选项对象解析成显示的 key
       */
      textKey: {
        type: String,
        default: 'name',
      },
      /**
       * 当点击选项的时候
       * @param {object} item 与 getTree 返回的 item 结构一样
       * @param {number} index 选项的 level index （index + 1 === level）
       */
      onSelectItem: {
        type: Function,
        required: true,
      },
      /**
       * 选项的级数
       */
      levelNums: {
        type: Number,
        default: 2,
      },
      /**
       * 选中的值列表, [level1Value, level2Value, level3Value...]
       */
      value: {
        type: Array,
        default: () => ([]),
      },
      /**
       * 解析是否是叶子节点的判断，
       * 非叶子节点会显示 '>' 小箭头
       *
       * 兼容历史版本，默认不开启
       */
      isLeaf: {
        type: Function,
        default: undefined,
      },
      /**
       * 同一层的选项是否能做前端的 fitler
       */
      filterAble: {
        type: Boolean,
        default: false,
      },
      /**
       * 选项 unique id key， 用来判断是否被选中，和 cache 的索引
       */
      identKey: {
        type: String,
        required: true,
      },
    },
    emits: ['reach-end'],
    data() {
      return {
        childrenState: {},
      }
    },
    computed: {
      contexts() {
        return getContext(
          this.levelNums,
          this.childrenState,
          this.value,
          this.identKey,
          this.isLeafCheck,
        )
      },
    },
    watch: {
      value: {
        handler(v) {
          v.forEach((item, index) => {
            if (!item) {
              return
            }
            if (this.isLeafCheck(item)) {
              this.$emit('reach-end', true)
              return
            }
            // To looup next context for each selectd item
            const lookupKey = getLookupkey(index + 1, item[this.identKey])
            const q = this.loadChildren({
              level: index + 2, // 比如 已有 0 index，则 load 的是 2 level
              lookupKey,
              parent: item,
            })
            if (v.length - 1 === index) {
              q.then(state => {
                if (state) {
                  const end = !state.errror && state.children.length < 1
                  /**
                   * 值是否被选中到底，某些情况下可能会校验必须选中到底
                   * @param {bool} end 是否到底了
                   */
                  this.$emit('reach-end', end)
                }
              })
            }
          })
        },
        immediate: true,
      },
    },
    created() {
      this.loadChildren({
        level: 1,
        lookupKey: ROOT_KEY,
      })
    },
    methods: {
      isLeafCheck(item) {
        if (this.isLeaf) {
          return this.isLeaf(item)
        }
        return false
      },
      handleSelect(item, index, event) {
        this.onSelectItem(item, index, event)
      },
      isSelected(item, index) {
        const val = this.value[index]
        if (!val) return null
        if (isNil(item[this.identKey])) {
          return null
        }
        return item[this.identKey] === val[this.identKey] ? true : null
      },
      setChildren(lookupKey, value, newV = {}) {
        this.childrenState = {
          ...this.childrenState,
          [lookupKey]: {
            lookupKey,
            ...value,
          },
          ...newV,
        }
        return this.childrenState
      },
      loadChildren({ level, lookupKey, parent }) {
        return loadChildrenAndSet(level, lookupKey, parent, {
          childrenState: this.childrenState,
          levelNums: this.levelNums,
          setChildren: this.setChildren,
          hasChildren: item => !this.isLeafCheck(item),
          identKey: this.identKey,
          getTree: this.getTree,
        })
      },
    },
  }
</script>
